import { state } from "../state";
import { apiCall } from "../api";
import { showToast, formatBoolean } from "../utils";

// -------------------- Mass columns --------------------
export const DEFAULT_MASS_COLUMNS = [
  "req_id", "ref_id", "title_full_req", "title_full_ref",
  "title_req_norm", "title_ref_norm", "strategy",
  "class", "subclass", "qty", "price", "warehouse", "territory",
  "qty_required", "qty_covered", "qty_shortage"
];

export let currentMassColumns: string[] | undefined = undefined;
export let currentMassShowAttributes = false;

async function loadMassColumnSettings(): Promise<void> {
  if (currentMassColumns !== undefined) return;
  const res = await apiCall("/api/mass_columns", "GET");
  currentMassColumns = res?.columns?.length ? res.columns : DEFAULT_MASS_COLUMNS;
  currentMassShowAttributes = !!res?.show_attributes;
}

let referenceColumnSettingsLoaded = false;

async function loadReferenceColumnSettings(): Promise<void> {
  if (referenceColumnSettingsLoaded) return;
  const res = await apiCall("/api/reference/column_settings", "GET");
  if (res && res.settings) {
    state.columnSettings = res.settings;
  }
  referenceColumnSettingsLoaded = true;
}

export function resetReferenceColumnSettingsCache(): void {
  referenceColumnSettingsLoaded = false;
}

export async function getMassColumns(): Promise<string[]> {
  await Promise.all([loadMassColumnSettings(), loadReferenceColumnSettings()]);
  return currentMassColumns || DEFAULT_MASS_COLUMNS;
}

function isWhitelistedAttribute(col: string): boolean {
  const whitelist = state.columnSettings || [];
  return whitelist.length > 0 && whitelist.includes(col);
}

export function filterMassColumns(columns: string[]): string[] {
  if (!columns || !columns.length) return columns;
  const massCols = currentMassColumns || DEFAULT_MASS_COLUMNS;
  const systemSet = new Set(DEFAULT_MASS_COLUMNS);
  return columns.filter((c: string) => {
    if (systemSet.has(c)) return massCols.includes(c);
    return isWhitelistedAttribute(c) || currentMassShowAttributes;
  });
}

export const DEFAULT_SINGLE_COLUMNS = [
  "req_id", "ref_id", "title_full_req", "title_full_ref",
  "title_req_norm", "title_ref_norm", "strategy",
  "class", "subclass", "qty", "price", "warehouse", "territory",
  "qty_required", "qty_covered", "qty_shortage"
];

export let currentSingleColumns: string[] | undefined = undefined;
export let currentSingleShowAttributes = false;

async function loadSingleColumnSettings(): Promise<void> {
  if (currentSingleColumns !== undefined) return;
  const res = await apiCall("/api/single_columns", "GET");
  currentSingleColumns = res?.columns?.length ? res.columns : DEFAULT_SINGLE_COLUMNS;
  currentSingleShowAttributes = !!res?.show_attributes;
}

export async function getSingleColumns(): Promise<string[]> {
  await Promise.all([loadSingleColumnSettings(), loadReferenceColumnSettings()]);
  return currentSingleColumns || DEFAULT_SINGLE_COLUMNS;
}

export function filterSingleColumns(columns: string[]): string[] {
  if (!columns || !columns.length) return columns;
  const singleCols = currentSingleColumns || DEFAULT_SINGLE_COLUMNS;
  const systemSet = new Set(DEFAULT_SINGLE_COLUMNS);
  return columns.filter((c: string) => {
    if (systemSet.has(c)) return singleCols.includes(c);
    return isWhitelistedAttribute(c) || currentSingleShowAttributes;
  });
}

export function resetMassColumnSettingsCache(): void {
  currentMassColumns = undefined;
  currentMassShowAttributes = false;
  resetReferenceColumnSettingsCache();
}

export function resetSingleColumnSettingsCache(): void {
  currentSingleColumns = undefined;
  currentSingleShowAttributes = false;
  resetReferenceColumnSettingsCache();
}

// -------------------- Column labels / display names --------------------
export const defaultSystemDict: Record<string, string> = {
  req_id: "id Потребность",
  ref_id: "id Запасы",
  title_full_req: "Наименование потребность",
  title_full_ref: "Наименование запасы",
  analog_counts: "Количество аналогов",
  strategy: "Стратегия сопоставления",
  title_req_norm: "Наименование потребность нормализованное",
  title_ref_norm: "Наименование запасы нормализованное",
  group: "Группа продукции",
  group_name: "Группа продукции",
  class: "Класс",
  class_name: "Класс",
  subclass: "Подкласс",
  subclass_name: "Подкласс",
  group_prod: "Группа",
  qty: "Количество (запасы)",
  price: "Цена",
  warehouse: "Склад",
  territory: "Территория",
  qty_required: "Требуемое количество",
  qty_covered: "Покрытое количество",
  qty_shortage: "Недостаток количества"
};

export let systemDict: Record<string, string> = { ...defaultSystemDict };

export function setSystemDict(dict: Record<string, string>): void {
  systemDict = dict;
}

export function getDisplayColumnName(col: string): string {
  if (!col) return col;
  const mp = state.colLabels?.master_parser || {};
  const sv = state.colLabels?.sveta_server || {};
  if (mp[col]) return mp[col];
  if (sv[col]) return sv[col];
  return systemDict[col] || col;
}

// -------------------- Virtual table --------------------
interface VirtualTableOptions {
  ajaxURL?: string;
  sampleData?: any[];
  editable?: boolean;
  rowSelection?: boolean;
  [key: string]: any;
}

export async function createVirtualTable(
  containerId: string,
  data: any[],
  columns: string[],
  options: VirtualTableOptions = {}
): Promise<any> {
  const container = document.getElementById(containerId);
  if (!container) return null;
  container.innerHTML = "";
  const sampleData = options.ajaxURL && data.length === 0 ? options.sampleData || [] : data;
  const isBoolCol = (col: string) => {
    for (const row of sampleData) {
      const v = row[col];
      if (v !== null && v !== undefined && v !== "") {
        return typeof v === "boolean";
      }
    }
    return false;
  };
  const boolFormatter = function(cell: any) {
    return formatBoolean(cell.getValue());
  };
  const tabColumns = columns.map((col: string) => {
    const colDef: any = {
      title: getDisplayColumnName(col),
      field: col,
      editor: options.editable ? "input" : false,
      headerFilter: "input"
    };
    if (isBoolCol(col)) {
      colDef.formatter = boolFormatter;
    }
    return colDef;
  });
  tabColumns.unshift({
    title: "№",
    formatter: "rownum",
    width: 50,
    headerSort: false,
    hozAlign: "center"
  });
  if (options.rowSelection) {
    tabColumns.unshift({
      formatter: "rowSelection",
      titleFormatter: function(cell: any, _formatterParams: any, _onRendered: any) {
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.style.width = "18px";
        checkbox.style.height = "18px";
        checkbox.style.cursor = "pointer";
        checkbox.style.margin = "0 auto";
        checkbox.style.display = "block";
        checkbox.addEventListener("click", function(e) {
          e.stopPropagation();
          const table = cell.getTable();
          const visibleRows = table.getRows("active");
          if (checkbox.checked) {
            visibleRows.forEach((r: any) => r.select());
          } else {
            visibleRows.forEach((r: any) => r.deselect());
          }
          if (typeof (window as any).updateRefBulkActions === "function") {
            (window as any).updateRefBulkActions();
          }
        });
        return checkbox;
      },
      hozAlign: "center",
      headerSort: false,
      width: 50,
      cellClick: function(_e: any, cell: any) {
        const row = cell.getRow();
        if (row) row.toggleSelect();
      }
    });
  }
  const tableConfig: any = {
    layout: "fitColumns",
    virtualDom: true,
    pagination: "local",
    paginationSize: 50,
    paginationSizeSelector: [50, 100, 200, 500],
    movableColumns: true,
    locale: "ru-ru",
    selectable: options.rowSelection ? true : false,
    langs: {
      "ru-ru": {
        pagination: {
          page_size: "Количество строк на странице",
          page_title: "Страница",
          first: "«",
          first_title: "Первая страница",
          last: "»",
          last_title: "Последняя страница",
          prev: "‹",
          prev_title: "Предыдущая страница",
          next: "›",
          next_title: "Следующая страница",
          all: "Все",
          counter: {
            showing: "Показано",
            of: "из",
            rows: "строк",
            pages: "страниц"
          }
        }
      }
    },
    columns: tabColumns
  };
  if (options.ajaxURL) {
    tableConfig.ajaxURL = options.ajaxURL;
    tableConfig.pagination = true;
    tableConfig.paginationMode = "remote";
    tableConfig.virtualDom = false;
    tableConfig.paginationButtonCount = 5;
    tableConfig.paginationCounter = "pages";
    const authToken = localStorage.getItem("token");
    if (authToken) {
      tableConfig.ajaxHeaders = { Authorization: `Bearer ${authToken}` };
    }
    tableConfig.ajaxResponse = function(_url: string, params: any, response: any) {
      response.last_page = Math.ceil(response.total / (response.per_page || response.size || params.size || 50));
      return response;
    };
    tableConfig.ajaxError = function(error: any) {
      console.error("[Tabulator] ajaxError:", error);
    };
  } else {
    tableConfig.data = data;
  }
  return new (window as any).Tabulator(container, { ...tableConfig, ...options });
}

// -------------------- Groups --------------------
interface GroupMeta {
  value: string;
  label: string;
}

export async function loadGroups(): Promise<void> {
  const data = await apiCall("/api/reference/groups", "GET");
  if (data && data.groups) {
    state.refGroupsMeta = data.groups as GroupMeta[];
    updateGroupSelects();
  }
}

export async function loadAllGroups(): Promise<void> {
  const data = await apiCall("/api/reference/groups?all_groups=true", "GET");
  if (data && data.groups) {
    const adminAttrSel = document.getElementById("admin-attr-group-select") as HTMLSelectElement | null;
    if (adminAttrSel) {
      adminAttrSel.innerHTML = (data.groups as GroupMeta[])
        .map((g) => `<option value="${g.value}">${g.label}</option>`)
        .join("");
    }
  }
}

export function updateGroupSelects(): void {
  const groups = state.refGroupsMeta as GroupMeta[];
  const selects = ["ref-group-select", "single-manual-group"];
  selects.forEach((id) => {
    const sel = document.getElementById(id) as HTMLSelectElement | null;
    if (!sel) return;
    sel.innerHTML = `<option value="">-- выберите --</option>` + groups
      .map((g) => `<option value="${g.value}">${g.label}</option>`)
      .join("");
  });
  const adminAttrSel = document.getElementById("admin-attr-group-select") as HTMLSelectElement | null;
  if (adminAttrSel) {
    adminAttrSel.innerHTML = groups
      .map((g) => `<option value="${g.value}">${g.label}</option>`)
      .join("");
  }
  const list = document.getElementById("mass-search-groups-list");
  if (list) {
    list.innerHTML = groups
      .map((g) => `
      <label class="dropdown-item">
        <input type="checkbox" value="${g.value}" data-label="${g.label}">
        <span>${g.label}</span>
      </label>
    `)
      .join("");
    updateMassGroupsToggleText();
  }
}

export function getSelectedMassGroups(): string[] {
  const list = document.getElementById("mass-search-groups-list");
  if (!list) return [];
  return Array.from(list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')).map((cb) => cb.value);
}

export function updateMassGroupsToggleText(): void {
  const btn = document.getElementById("mass-search-groups-toggle");
  if (!btn) return;
  const selected = getSelectedMassGroups();
  const groups = state.refGroupsMeta as GroupMeta[];
  if (!selected.length) {
    btn.innerText = "Выберите группы";
  } else if (selected.length === 1) {
    const label = groups.find((g) => g.value === selected[0])?.label || selected[0];
    btn.innerText = label;
  } else {
    btn.innerText = `Выбрано ${selected.length} групп`;
  }
}

export function autoSelectMassGroups(): void {
  const list = document.getElementById("mass-search-groups-list");
  if (!list) return;
  list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
    cb.checked = state.groupList.includes(cb.value);
  });
  updateMassGroupsToggleText();
}

// -------------------- Dual-list init --------------------
export function dualListInit(availId: string, selId: string, allItems: string[], selectedItems: string[]): void {
  const avail = document.getElementById(availId) as HTMLSelectElement | null;
  const sel = document.getElementById(selId) as HTMLSelectElement | null;
  if (!avail || !sel) return;
  const selSet = new Set(selectedItems);
  avail.innerHTML = allItems
    .filter((c) => !selSet.has(c))
    .map((c) => `<option value="${c}">${c}</option>`)
    .join("");
  sel.innerHTML = selectedItems
    .map((c) => `<option value="${c}">${c}</option>`)
    .join("");
}
